/**
 * Copyright 2026 GDG on Campus Farmingdale State College
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Parses and constructs a formatted string representing the code location, including the file path,
 * associated entity name (such as a function, class, component, or decorator), and human context.
 *
 * The result is useful for debugging, developer logging, and traceability—matching the entity (function, class, etc.)
 * and the context in source code references.
 *
 * @template C The type of the context value.
 * @template T The type of the entity parameter.
 * @param {C} context - A description, situation, or custom value relevant to this code path.
 * @param {T} entity - The entity whose name is included; may be a function, class, object, component, decorator, or their name.
 * @returns {string} A formatted string: "location: <path> @<entity>: <context>".
 * @throws {Error} This function does not throw directly, but see {@link getFilePath}, which may throw in rare stack-trace parsing errors.
 * @example
 * // Function usage
 * function myFunction() {}
 * parseCodePath('initialization', myFunction);
 * // => "location: /current/dir/file.js @myFunction: initialization"
 *
 * // Class usage
 * class MyClass {}
 * parseCodePath('instantiating MyClass', MyClass);
 * // => "location: ... @MyClass: instantiating MyClass"
 *
 * // String as entity
 * parseCodePath('some context', 'EntityAsString');
 * // => "location: ... @EntityAsString: some context"
 *
 * @author Mike Odnis
 * @author WomB0ComB0
 * @see parseCodePathDetailed

 * @readonly
 * @public
 * @version 1.0.0
 */
export const parseCodePath = (context, entity) => {
    const entityName = extractEntityName(entity);
    const filePath = getFilePath();
    return `location: ${filePath} @${entityName}: ${context}`;
};
/**
 * Extracts a standardized name from an entity, supporting functions, classes, instances, symbols, and strings.
 *
 * - For functions or classes, returns the function's/class's `name` or 'AnonymousFunction'
 * - For instances, extracts the class/constructor name
 * - For string entities, returns the string itself
 * - For symbols, returns their string representation
 * - Returns 'UnknownEntity' if no name is found
 *
 * @template T The type of the entity.
 * @param {T} entity - The entity to extract a name from.
 * @returns {string} The extracted name or 'UnknownEntity' if not determinable.
 * @example
 * extractEntityName(function test() {}); // 'test'
 * extractEntityName(class MyClass {}); // 'MyClass'
 * extractEntityName('ManualName'); // 'ManualName'
 * extractEntityName(Symbol('sym')); // 'Symbol(sym)'
 *
 * @author Mike Odnis
 * @readonly
 * @public
 * @version 1.0.0
 */
function extractEntityName(entity) {
    if (typeof entity === 'function') {
        return entity.name || 'AnonymousFunction';
    }
    if (typeof entity === 'object' && entity !== null) {
        const entityConstructor = entity.constructor;
        if (typeof entityConstructor === 'function' && entityConstructor.name) {
            return entityConstructor.name;
        }
    }
    if (typeof entity === 'string') {
        return entity;
    }
    if (typeof entity === 'symbol') {
        return entity.toString();
    }
    return 'UnknownEntity';
}
/**
 * Attempts to determine the current file path in both Node.js and browser-like environments.
 * Utilizes `__filename` if available (Node), or parses the call stack when not.
 * Falls back to process.cwd() or 'unknown-location' if all else fails.
 *
 * @returns {string} The current file path, URL, or 'unknown-location'.
 * @throws {Error} Throws if unexpected errors occur in stack trace parsing (caught internally, returns 'unknown-location').
 * @example
 * getFilePath(); // "/Users/user/project/src/utils/formatting/parse-code-path.js"
 *
 * @author Mike Odnis
 * @web
 * @readonly
 * @private
 * @version 1.0.0
 */
function getFilePath() {
    try {
        if (typeof __filename !== 'undefined') {
            return __filename;
        }
        const stack = new Error('Stack trace for file path extraction').stack;
        if (stack) {
            const stackLine = stack.split('\n')[2]; // Get caller's line
            const match = /https?:\/\/[^)]+/.exec(stackLine) ||
                /file:\/\/[^)]+/.exec(stackLine) ||
                /at\s+(.+):\d+:\d+/.exec(stackLine);
            if (match) {
                return match[1] || match[0];
            }
        }
        return process?.cwd?.() || 'unknown-location';
    }
    catch {
        return 'unknown-location';
    }
}
/**
 * Parses and constructs a detailed formatted location string, including file path, entity, and context,
 * with optional line number, ISO timestamp, and custom prefix control. Useful for enhanced debugging or audit logs.
 *
 * @template C The type of the context parameter.
 * @template T The type of entity (function, class, etc.).
 * @param {C} context - Context description of the operation or location.
 * @param {T} entity - The entity whose name is included.
 * @param {object} [options] - Optional configuration for output.
 * @param {boolean} [options.includeLineNumber] - If true, appends the call site line number.
 * @param {boolean} [options.includeTimestamp] - If true, appends an ISO 8601 timestamp.
 * @param {string}  [options.customPrefix] - Custom prefix instead of default "location".
 * @returns {string} Detailed formatted location string.
 * @throws {Error} Does not throw directly but relies on internal getFilePath logic.
 * @example
 * parseCodePathDetailed('init', MyClass, {includeLineNumber: true, includeTimestamp: true});
 * // => "location: ...:42 [2024-06-01T08:00:00.000Z] @MyClass: init"
 *
 * @author Mike Odnis
 * @author WomB0ComB0
 * @see parseCodePath

 * @readonly
 * @public
 * @version 1.0.0
 */
export const parseCodePathDetailed = (context, entity, options = {}) => {
    const entityName = extractEntityName(entity);
    const filePath = getFilePath();
    const prefix = options.customPrefix || 'location';
    let locationInfo = `${prefix}: ${filePath}`;
    if (options.includeLineNumber) {
        try {
            const stack = new Error('Stack trace for line number extraction').stack;
            const callerLine = stack?.split('\n')[2];
            const lineMatch = callerLine?.match(/:(\d+):\d+/);
            if (lineMatch) {
                locationInfo += `:${lineMatch[1]}`;
            }
        }
        catch {
            // Ignore errors in line number extraction
        }
    }
    if (options.includeTimestamp) {
        locationInfo += ` [${new Date().toISOString()}]`;
    }
    return `${locationInfo} @${entityName}: ${context}`;
};
//# sourceMappingURL=parse-code-path.js.map